/**
 * @module features/search/entity-search
 * @description Entity-specific search functionality for single entity type search
 * Handles searching within a specific entity type with debouncing and result display
 */

import { searchEntities } from '../../core/api/entity-api.js';
import { ENTITY_TYPES } from '../entity/entity-types.js';
import { escapeHtml } from '../../utils/html-utils.js';
import { logger } from '../../utils/logger.js';
import { safeMonitoring as monitoring, ErrorCategory, ErrorSeverity } from '../../services/monitoring-safe.js';

// Search configuration
const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Initialize entity-specific search (replaces Select2 implementation)
 * Sets up search UI and event listeners for entity-specific search
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {Object} user - Firebase user object
 * @param {Function} onSelectCallback - Callback when entity is selected (entityType, entityId, user)
 */
export function initializeEntitySearch(entityType, user, onSelectCallback) {
    const searchInput = document.getElementById('entity-search-input');
    const searchBtn = document.getElementById('entity-search-btn');
    const resultsSection = document.getElementById('entity-search-results');
    const resultsContainer = document.getElementById('entity-results-container');
    const clearBtn = document.getElementById('entity-clear-search-btn');
    const errorDiv = document.getElementById('entity-search-error');

    if (!searchInput || !searchBtn || !resultsSection || !resultsContainer) {
        logger.warn('Entity search elements not found');
        return;
    }

    let searchTimeout = null;

    // Clear any previous event listeners by cloning and replacing
    const newSearchInput = searchInput.cloneNode(true);
    searchInput.parentNode.replaceChild(newSearchInput, searchInput);
    const newSearchBtn = searchBtn.cloneNode(true);
    searchBtn.parentNode.replaceChild(newSearchBtn, searchBtn);
    const newClearBtn = clearBtn.cloneNode(true);
    clearBtn.parentNode.replaceChild(newClearBtn, clearBtn);

    // Input handler with debouncing
    newSearchInput.addEventListener('input', (e) => {
        const searchTerm = e.target.value.trim();

        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Hide results if below minimum characters
        if (searchTerm.length < SEARCH_MIN_CHARS) {
            resultsSection.classList.add('hidden');
            if (errorDiv) errorDiv.classList.add('hidden');
            return;
        }

        // Debounce search
        searchTimeout = setTimeout(() => {
            performEntitySearch(entityType, searchTerm, user, resultsContainer, resultsSection, errorDiv, onSelectCallback);
        }, SEARCH_DEBOUNCE_MS);
    });

    // Button click handler
    newSearchBtn.addEventListener('click', () => {
        const searchTerm = newSearchInput.value.trim();
        if (searchTerm.length >= SEARCH_MIN_CHARS) {
            performEntitySearch(entityType, searchTerm, user, resultsContainer, resultsSection, errorDiv, onSelectCallback);
        } else {
            showEntitySearchError(`Please enter at least ${SEARCH_MIN_CHARS} characters to search`, errorDiv, resultsSection);
        }
    });

    // Enter key handler
    newSearchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const searchTerm = newSearchInput.value.trim();
            if (searchTerm.length >= SEARCH_MIN_CHARS) {
                performEntitySearch(entityType, searchTerm, user, resultsContainer, resultsSection, errorDiv, onSelectCallback);
            } else {
                showEntitySearchError(`Please enter at least ${SEARCH_MIN_CHARS} characters to search`, errorDiv, resultsSection);
            }
        }
    });

    // Clear button handler
    newClearBtn.addEventListener('click', () => {
        newSearchInput.value = '';
        resultsSection.classList.add('hidden');
        if (errorDiv) errorDiv.classList.add('hidden');
        newSearchInput.focus();
    });

    // Escape key to clear
    newSearchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            newSearchInput.value = '';
            resultsSection.classList.add('hidden');
            if (errorDiv) errorDiv.classList.add('hidden');
        }
    });

    // Focus the input
    newSearchInput.focus();
}

/**
 * Perform entity search and display results
 * @param {string} entityType - Type of entity
 * @param {string} searchTerm - Search term
 * @param {Object} user - Firebase user object
 * @param {HTMLElement} resultsContainer - Results container element
 * @param {HTMLElement} resultsSection - Results section element
 * @param {HTMLElement} errorDiv - Error message element
 * @param {Function} onSelectCallback - Callback when entity is selected
 */
export async function performEntitySearch(entityType, searchTerm, user, resultsContainer, resultsSection, errorDiv, onSelectCallback) {
    try {
        // Clear any previous error
        if (errorDiv) errorDiv.classList.add('hidden');

        // Show loading state
        resultsContainer.innerHTML = '<div class="loading-indicator"><div class="spinner"></div><p>Searching...</p></div>';
        resultsSection.classList.remove('hidden');

        // Perform search via API
        const results = await searchEntities(entityType, searchTerm);

        // Display results
        displayEntitySearchResults(entityType, results, searchTerm, resultsContainer, user, onSelectCallback);

    } catch (error) {
        logger.error(`Error searching ${entityType}:`, error);

        // Track search error
        if (monitoring.isInitialized()) {
            monitoring.trackError(error, {
                category: ErrorCategory.SEARCH,
                severity: ErrorSeverity.ERROR,
                operation: 'handleEntitySearch',
                entityType: entityType,
                searchTerm: searchTerm
            });
        }

        showEntitySearchError(`Failed to search ${entityType}: ${error.message}`, errorDiv, resultsSection);
        resultsContainer.innerHTML = '';
    }
}

/**
 * Display entity search results
 * @param {string} entityType - Type of entity
 * @param {Array} results - Search results
 * @param {string} searchTerm - Search term for highlighting
 * @param {HTMLElement} resultsContainer - Results container element
 * @param {Object} user - Firebase user object
 * @param {Function} onSelectCallback - Callback when entity is selected
 */
export function displayEntitySearchResults(entityType, results, searchTerm, resultsContainer, user, onSelectCallback) {
    const config = ENTITY_TYPES[entityType];

    if (!results || results.length === 0) {
        resultsContainer.innerHTML = `
            <div class="no-results">
                <p>No ${config.label.toLowerCase()}s found matching "${escapeHtml(searchTerm)}"</p>
            </div>
        `;
        return;
    }

    const highlightTerm = (text, term) => {
        if (!term) return escapeHtml(text);
        const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return escapeHtml(text).replace(regex, '<mark>$1</mark>');
    };

    let html = `<div class="entity-results-list">`;

    results.forEach(entity => {
        const entityId = entity.id || entity.Id;
        const entityName = entity.name || entity.Name;

        html += `
            <div class="entity-result-item" data-entity-type="${entityType}" data-entity-id="${escapeHtml(entityId)}">
                <div class="entity-result-icon">${config.icon}</div>
                <div class="entity-result-content">
                    <div class="entity-result-name">${highlightTerm(entityName, searchTerm)}</div>
                    <div class="entity-result-id">ID: ${escapeHtml(entityId)}</div>
                </div>
                <div class="entity-result-arrow">→</div>
            </div>
        `;
    });

    html += `</div>`;
    html += `<div class="results-count">${results.length} ${config.label.toLowerCase()}${results.length !== 1 ? 's' : ''} found</div>`;

    resultsContainer.innerHTML = html;

    // Add click event listeners to result items
    const resultItems = resultsContainer.querySelectorAll('.entity-result-item');
    resultItems.forEach(item => {
        item.addEventListener('click', () => {
            const entityType = item.dataset.entityType;
            const entityId = item.dataset.entityId;

            // Call the callback with entity information
            if (onSelectCallback && typeof onSelectCallback === 'function') {
                onSelectCallback(entityType, entityId, user);
            }
        });
    });
}

/**
 * Show entity search error message
 * @param {string} message - Error message
 * @param {HTMLElement} errorDiv - Error div element
 * @param {HTMLElement} resultsSection - Results section element
 */
export function showEntitySearchError(message, errorDiv, resultsSection) {
    if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.classList.remove('hidden');
    }
    if (resultsSection) {
        resultsSection.classList.add('hidden');
    }
}

/**
 * Clear entity search state
 * Resets search input and hides results
 */
export function clearEntitySearch() {
    const searchInput = document.getElementById('entity-search-input');
    const resultsSection = document.getElementById('entity-search-results');
    const resultsContainer = document.getElementById('entity-results-container');
    const errorDiv = document.getElementById('entity-search-error');

    if (searchInput) searchInput.value = '';
    if (resultsSection) resultsSection.classList.add('hidden');
    if (resultsContainer) resultsContainer.innerHTML = '';
    if (errorDiv) errorDiv.classList.add('hidden');
}

/**
 * Update entity search UI labels for specific entity type
 * @param {string} entityType - Type of entity
 */
export function updateEntitySearchUI(entityType) {
    const config = ENTITY_TYPES[entityType];

    if (!config) {
        logger.error('Unknown entity type:', entityType);

        // Track invalid entity type
        if (monitoring.isInitialized()) {
            monitoring.trackError(new Error(`Unknown entity type: ${entityType}`), {
                category: ErrorCategory.VALIDATION,
                severity: ErrorSeverity.ERROR,
                operation: 'updateEntitySearchUI',
                entityType: entityType
            });
        }

        return;
    }

    // Update the UI with entity-specific labels
    const entitySearchIcon = document.getElementById('entity-search-icon');
    const entitySearchTitle = document.getElementById('entity-search-title');
    const entitySearchTypeLabel = document.getElementById('entity-search-type-label');
    const entitySearchBtnText = document.getElementById('entity-search-btn-text');
    const entitySearchInput = document.getElementById('entity-search-input');

    if (entitySearchIcon) entitySearchIcon.textContent = config.icon;
    if (entitySearchTitle) entitySearchTitle.textContent = `${config.label  }s`;
    if (entitySearchTypeLabel) entitySearchTypeLabel.textContent = config.singular;
    if (entitySearchBtnText) entitySearchBtnText.textContent = `Search ${config.label}s`;
    if (entitySearchInput) entitySearchInput.placeholder = `${config.searchPlaceholder} (minimum 3 characters)...`;
}
